import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getKubernetesNodepools } from '@/modules/Service/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

/** Status chip color mapping consistent with SERVICE_STATUS_OPTIONS pattern */
const statusColorMap: Record<string, string> = {
    active: 'success',
    running: 'success',
    Ready: 'success',
    provisioning: 'warning',
    pending: 'warning',
    creating: 'warning',
    Updating: 'warning',
    error: 'error',
    failed: 'error',
    stopped: 'error',
    Deleted: 'error',
    Draining: 'warning',
    Terminating: 'error',
}

interface KubernetesCluster {
    id: string
    name: string
    uid: string
    status: string
    created_at: string
    metadata: {
        version?: string
        runtime?: string
        network?: {
            cni?: string
            pod_cidr?: string
            service_cidr?: string
        }
        pools?: {
            name: string
            roles: string
            type: string
            flavor: string
            cores: number
            memory: number
            volumes: number
            min: number
            max: number
            autoscaling: boolean
            machines?: { name: string }[]
        }[]
        addons?: Record<string, { enabled?: boolean; type?: string }>
    }
}

interface Nodepool {
    id: string
    name: string
    currentSize: number
    desiredSize: number
    minSize: number
    maxSize: number
    actualMinSize: number
    actualMaxSize: number
    instanceType: string
    zone: string
    status: string
    totalNodes: number
    autoscaling: boolean
    autoscalingClass: string
    createdAt: string
    updatedAt: string
    cores: number
    memory: number
    volumes: number
}

interface KubernetesAddonCardProps {
    addon: {
        service_id: number
        service_name: string
        service_uuid: string
        provider_scope: string
        clusters?: KubernetesCluster[]
    }
    serviceID: number
}

/** Sub-component that fetches and renders nodepools for a single cluster */
const ClusterNodepools = ({
    serviceID,
    addonUUID,
    cluster,
}: {
    serviceID: number
    addonUUID: string
    cluster: KubernetesCluster
}) => {
    const { t } = useTranslation('services')

    const { data: nodepoolData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_KUBERNETES_NODEPOOLS,
            serviceID,
            cluster.id,
        ],
        queryFn: () =>
            getKubernetesNodepools({
                serviceID,
                addonUUID,
                clusterID: cluster.id,
            }),
        enabled: !!addonUUID && !!cluster.id,
    })

    const nodepools: Nodepool[] = nodepoolData?.nodepools || []
    const meta = cluster.metadata
    const network = meta?.network
    const enabledAddons = Object.entries(meta?.addons || {})
        .filter(([, v]) => v.enabled)
        .map(([k]) => k.replace(/_/g, ' '))

    // Compute totals from API nodepools
    const totals = nodepools.reduce(
        (acc, pool) => {
            acc.nodes += pool.currentSize
            acc.cores += pool.cores * pool.currentSize
            acc.memory += pool.memory * pool.currentSize
            acc.storage += pool.volumes * pool.currentSize
            return acc
        },
        { nodes: 0, cores: 0, memory: 0, storage: 0 }
    )

    return (
        <div key={cluster.id} className="border border-gray-200 rounded-md p-3">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <Typogaphy className="font-medium text-gray-900">
                    {cluster.name}
                </Typogaphy>
                {meta?.version && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={meta.version}
                        className="text-xs"
                    />
                )}
                <Chip
                    size="small"
                    variant="contained"
                    color={statusColorMap[cluster.status] || 'info'}
                    label={cluster.status}
                    className="capitalize"
                />
                {enabledAddons.map((name) => (
                    <Chip
                        key={name}
                        size="small"
                        variant="outlined"
                        color="info"
                        label={name}
                        className="capitalize"
                    />
                ))}
            </div>

            {/* Resource stats as small blocks */}
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-2">
                {[
                    {
                        label: t('nodes', { defaultValue: 'Nodes' }),
                        value: totals.nodes,
                    },
                    { label: 'CPU', value: `${totals.cores} vCores` },
                    {
                        label: t('memory', { defaultValue: 'Mem' }),
                        value: `${totals.memory} GB`,
                    },
                    {
                        label: t('storage', { defaultValue: 'Vol' }),
                        value: `${totals.storage} GB`,
                    },
                    ...(network?.cni
                        ? [{ label: 'CNI', value: network.cni }]
                        : []),
                    ...(meta?.runtime
                        ? [{ label: 'Runtime', value: meta.runtime }]
                        : []),
                    ...(network?.pod_cidr
                        ? [{ label: 'Pod CIDR', value: network.pod_cidr }]
                        : []),
                    ...(network?.service_cidr
                        ? [{ label: 'Svc CIDR', value: network.service_cidr }]
                        : []),
                ].map((stat) => (
                    <div
                        key={stat.label}
                        className="bg-gray-100 rounded-sm px-2 py-1.5"
                    >
                        <Typogaphy className="text-[10px] text-gray-500 uppercase leading-none">
                            {stat.label}
                        </Typogaphy>
                        <Typogaphy className="text-xs font-semibold text-gray-900 mt-0.5">
                            {stat.value}
                        </Typogaphy>
                    </div>
                ))}
            </div>

            {/* Worker pools table */}
            {nodepools.length > 0 && (
                <div className="overflow-x-auto rounded-md border border-gray-200">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                                <th className="px-3 py-1.5">
                                    {t('name', { defaultValue: 'Name' })}
                                </th>
                                <th className="px-3 py-1.5">
                                    {t('status', { defaultValue: 'Status' })}
                                </th>
                                <th className="px-3 py-1.5">Flavor</th>
                                <th className="px-3 py-1.5 text-right">CPU</th>
                                <th className="px-3 py-1.5 text-right">Mem</th>
                                <th className="px-3 py-1.5 text-right">Vol</th>
                                <th className="px-3 py-1.5 text-right">
                                    {t('nodes', { defaultValue: 'Nodes' })}
                                </th>
                                <th className="px-3 py-1.5 text-right">Min</th>
                                <th className="px-3 py-1.5 text-right">Max</th>
                                <th className="px-3 py-1.5">AS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {nodepools.map((pool) => (
                                <tr key={pool.id} className="text-gray-700">
                                    <td className="px-3 py-1.5 font-medium">
                                        {pool.name}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <Chip
                                            size="small"
                                            variant="contained"
                                            color={
                                                statusColorMap[pool.status] ||
                                                'info'
                                            }
                                            label={pool.status}
                                        />
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {pool.instanceType}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.cores}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.memory}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.volumes}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.currentSize}
                                        {pool.desiredSize !==
                                            pool.currentSize && (
                                            <span className="text-gray-400 ml-1">
                                                →{pool.desiredSize}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.actualMinSize}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {pool.actualMaxSize}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <Chip
                                            size="small"
                                            variant="contained"
                                            color={
                                                pool.autoscaling
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                            label={
                                                pool.autoscaling ? 'On' : 'Off'
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

const KubernetesAddonCard = ({
    addon,
    serviceID,
}: KubernetesAddonCardProps) => {
    const { t } = useTranslation('services')

    if (!addon.clusters || addon.clusters.length === 0) {
        return (
            <Typogaphy className="text-sm text-gray-500">
                {addon.service_name} —{' '}
                {t('no-clusters', { defaultValue: 'No clusters' })}
            </Typogaphy>
        )
    }

    return (
        <div className="space-y-3">
            {addon.clusters.map((cluster) => (
                <ClusterNodepools
                    key={cluster.id}
                    serviceID={serviceID}
                    addonUUID={addon.service_uuid}
                    cluster={cluster}
                />
            ))}
        </div>
    )
}

export default KubernetesAddonCard
